/**
 * [LPR 테스트 페이지]
 * 두 가지 모드로 우리 LPR(yolo) 검증:
 *   - 업로드 모드: 정지 이미지 → POST /kiosk/lpr-recognize-upload
 *   - 라이브 모드: 카메라 선택 → 자동/수동 GET /kiosk/lpr-test?port=N
 *
 * 인식된 plate는 하단 "인증 시뮬레이션"에 자동 채움 → /kiosk/auth로 보정 동작 확인.
 *
 * 운영 LPR(외주)과 무관 — 우리 yolo 정확도/보정 정성 확인 용도.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import api from '../api/client';
import { getApiOrigin } from '../api/config';

// ──────────────────────────────────────────────
// 카메라 메타 (CameraPage와 동일)
// ──────────────────────────────────────────────
const ALL_CAMERAS = [
  { id: 1, label: '세차기1-1', port: 90,  storeCode: 'jeonju' },
  { id: 2, label: '세차기1-2', port: 100, storeCode: 'jeonju' },
  { id: 3, label: '세차기2-1', port: 120, storeCode: 'jeonju' },
  { id: 4, label: '세차기2-2', port: 130, storeCode: 'jeonju' },
  { id: 5, label: '세차기3-1', port: 140, storeCode: 'jeonju' },
  { id: 6, label: '세차기3-2', port: 150, storeCode: 'jeonju' },
];

const API_ORIGIN = getApiOrigin();
const AUTO_LPR_INTERVAL_MS = 5000;

// ──────────────────────────────────────────────
// 인식 결과 카드 (업로드/라이브 공용)
// ──────────────────────────────────────────────
function RecogResultCard({ result }) {
  if (!result) return null;
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: '8px',
        backgroundColor: result.plate ? '#e8f5e9' : '#fce4ec',
        border: `1px solid ${result.plate ? '#a5d6a7' : '#f48fb1'}`,
      }}
    >
      {result.plate ? (
        <>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 22,
              letterSpacing: 2,
              fontFamily: 'monospace',
              color: '#2e7d32',
            }}
          >
            {result.plate}
          </Typography>
          {result.confidence != null && (
            <Typography variant="caption" sx={{ color: '#558b2f' }}>
              신뢰도 {Math.round(result.confidence * 100)}%
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: '#c62828' }}>
          {result.error || '번호판 인식 실패'}
        </Typography>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────
// 모드 1: 업로드
// ──────────────────────────────────────────────
function UploadMode({ onRecognized }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const [recogResult, setRecogResult] = useState(null);
  const fileInputRef = useRef(null);

  const acceptFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) return alert('이미지 파일만 업로드할 수 있어요.');
    if (f.size > 8 * 1024 * 1024) return alert('이미지는 8MB 이하만 가능해요.');
    setFile(f);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setRecogResult(null);
  };

  const handleRecognize = async () => {
    if (!file) return;
    setRecognizing(true);
    setRecogResult(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post('/kiosk/lpr-recognize-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setRecogResult(res.data);
      if (res.data?.plate) onRecognized(res.data.plate);
    } catch (err) {
      setRecogResult({
        plate: null,
        error: err.response?.data?.message || err.message || '요청 실패',
      });
    } finally {
      setRecognizing(false);
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          border: dragOver ? '2px dashed #1976d2' : '2px dashed #cfd8dc',
          backgroundColor: dragOver ? '#e3f2fd' : '#fafafa',
          borderRadius: '12px',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          acceptFile(e.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => acceptFile(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        <CloudUploadIcon sx={{ fontSize: 40, color: '#90a4ae', mb: 1 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {file ? file.name : '이미지를 드래그하거나 클릭해서 선택하세요 (JPG/PNG, 최대 8MB)'}
        </Typography>
      </Paper>

      {previewUrl && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            <Box
              component="img"
              src={previewUrl}
              alt="preview"
              sx={{
                maxWidth: { xs: '100%', md: 360 },
                maxHeight: 240,
                objectFit: 'contain',
                borderRadius: '8px',
                backgroundColor: '#000',
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Button
                variant="contained"
                onClick={handleRecognize}
                disabled={recognizing}
                startIcon={
                  recognizing ? <CircularProgress size={16} color="inherit" /> : <DirectionsCarIcon />
                }
                fullWidth
                sx={{ mb: 1.5 }}
              >
                {recognizing ? '인식 중...' : '번호판 인식'}
              </Button>
              <RecogResultCard result={recogResult} />
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────
// 모드 2: 라이브 카메라
// ──────────────────────────────────────────────
function LiveMode({ onRecognized }) {
  const [cameraId, setCameraId] = useState(1);
  const [autoLpr, setAutoLpr] = useState(false);
  const [recognizing, setRecognizing] = useState(false);
  const [recogResult, setRecogResult] = useState(null);
  const [snapshotError, setSnapshotError] = useState(false);
  const [timestamp, setTimestamp] = useState(Date.now());
  const loadingRef = useRef(false); // 자동 인식 중복 호출 방지

  const camera = ALL_CAMERAS.find((c) => c.id === cameraId) ?? ALL_CAMERAS[0];

  // 1초마다 스냅샷 URL 갱신 (image element re-fetch)
  useEffect(() => {
    const i = setInterval(() => setTimestamp(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  // 카메라 바뀌면 상태 리셋
  useEffect(() => {
    setSnapshotError(false);
    setRecogResult(null);
  }, [cameraId]);

  const doRecognize = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setRecognizing(true);
    try {
      const res = await api.get(`/kiosk/lpr-test?port=${camera.port}`);
      // 자동 모드에서 인식 실패한 결과는 이전 결과를 덮어쓰지 않음 (깜박임 방지)
      if (res.data?.plate || !autoLpr) {
        setRecogResult(res.data);
      }
      if (res.data?.plate) onRecognized(res.data.plate);
    } catch (err) {
      if (!autoLpr) {
        setRecogResult({
          plate: null,
          error: err.response?.data?.message || err.message || '요청 실패',
        });
      }
    } finally {
      loadingRef.current = false;
      setRecognizing(false);
    }
  }, [camera.port, autoLpr, onRecognized]);

  // 자동 인식 인터벌
  useEffect(() => {
    if (!autoLpr) return;
    doRecognize();
    const i = setInterval(doRecognize, AUTO_LPR_INTERVAL_MS);
    return () => clearInterval(i);
  }, [autoLpr, doRecognize]);

  const snapshotUrl = `${API_ORIGIN}/api/v1/kiosk/snapshot?port=${camera.port}&t=${timestamp}`;

  return (
    <Box>
      {/* 컨트롤 행 */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select
          size="small"
          value={cameraId}
          onChange={(e) => setCameraId(Number(e.target.value))}
          sx={{ minWidth: 140 }}
        >
          {ALL_CAMERAS.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.label} (:{c.port})</MenuItem>
          ))}
        </Select>
        <Button
          variant={autoLpr ? 'contained' : 'outlined'}
          color={autoLpr ? 'primary' : 'inherit'}
          startIcon={autoLpr ? <StopIcon /> : <PlayArrowIcon />}
          onClick={() => setAutoLpr((v) => !v)}
        >
          {autoLpr ? '자동 인식 중지' : `자동 인식 (${AUTO_LPR_INTERVAL_MS / 1000}초)`}
        </Button>
        <Button
          variant="outlined"
          startIcon={recognizing ? <CircularProgress size={14} /> : <DirectionsCarIcon />}
          onClick={doRecognize}
          disabled={recognizing}
        >
          지금 인식
        </Button>
      </Box>

      {/* 스냅샷 + 결과 오버레이 */}
      <Paper sx={{ position: 'relative', overflow: 'hidden', backgroundColor: '#111' }}>
        <Box sx={{ position: 'relative', minHeight: 360 }}>
          {/* img를 항상 mount해서 timestamp 갱신마다 자동 재시도 — onLoad/onError가 snapshotError 상태를 토글 */}
          <Box
            component="img"
            src={snapshotUrl}
            alt={camera.label}
            onError={() => setSnapshotError(true)}
            onLoad={() => setSnapshotError(false)}
            sx={{
              width: '100%',
              display: 'block',
              maxHeight: 540,
              objectFit: 'contain',
              // 에러 상태일 때는 깨진 아이콘이 보이지 않도록 시각만 숨김 (네트워크 fetch는 계속)
              visibility: snapshotError ? 'hidden' : 'visible',
              minHeight: snapshotError ? 360 : 'auto',
            }}
          />
          {snapshotError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                p: 4,
                textAlign: 'center',
                color: 'rgba(255,255,255,0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'center',
              }}
            >
              <VideocamOffIcon sx={{ fontSize: 40 }} />
              <Typography variant="body2">스냅샷을 가져올 수 없습니다.</Typography>
              <Typography variant="caption" sx={{ maxWidth: 480, lineHeight: 1.7 }}>
                백엔드(:3000)가 실행 중인지, <code>CAMERA_HOST</code> 환경변수가 카메라 IP를 가리키는지,
                현재 네트워크에서 카메라에 접근 가능한지 확인이 필요합니다.
              </Typography>
            </Box>
          )}

          {/* 인식 결과 오버레이 */}
          {recogResult?.plate && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 12,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.78)',
                borderRadius: '8px',
                px: 2.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: '1px solid rgba(76,175,80,0.6)',
              }}
            >
              <DirectionsCarIcon sx={{ color: '#66bb6a', fontSize: 18 }} />
              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 22,
                  letterSpacing: 2,
                  fontFamily: 'monospace',
                }}
              >
                {recogResult.plate}
              </Typography>
              {recogResult.confidence != null && (
                <Typography sx={{ color: '#81c784', fontSize: 12, fontWeight: 600 }}>
                  {Math.round(recogResult.confidence * 100)}%
                </Typography>
              )}
            </Box>
          )}

          {/* 자동 모드 처리 중 스피너 */}
          {autoLpr && recognizing && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.55)',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
              }}
            >
              <CircularProgress size={16} sx={{ color: '#90caf9' }} />
            </Box>
          )}
        </Box>
      </Paper>

      {/* 인식 실패/에러 텍스트 (오버레이 외 영역에 표시) */}
      {recogResult && !recogResult.plate && (
        <Box sx={{ mt: 1 }}>
          <RecogResultCard result={recogResult} />
        </Box>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────
// 인증 시뮬레이션 (공용 — 업로드/라이브 결과로 채움)
// ──────────────────────────────────────────────
function AuthSimulation({ plate, setPlate }) {
  const [authing, setAuthing] = useState(false);
  const [authResult, setAuthResult] = useState(null);

  const handleAuth = async () => {
    if (!plate.trim()) return;
    setAuthing(true);
    setAuthResult(null);
    try {
      const res = await api.post('/kiosk/auth', { licensePlate: plate.trim() });
      setAuthResult(res.data);
    } catch (err) {
      setAuthResult({
        authorized: false,
        reason: err.response?.data?.message || err.message || '요청 실패',
      });
    } finally {
      setAuthing(false);
    }
  };

  return (
    <Paper sx={{ mt: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <VerifiedUserIcon sx={{ color: '#1976d2' }} />
        <Typography variant="h6" fontWeight={700}>인증 시뮬레이션</Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
        plate를 수정해서 등록 차량과 1글자 다르게 시도하면 백엔드 보정(corrected/originalPlate)이 어떻게
        동작하는지 확인할 수 있어요. 인증 성공 시 실제로 WashRecord가 생성되니 본인 차로 테스트하면
        그 날 1회 제한에 걸립니다.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="예: 12가3456"
          value={plate}
          onChange={(e) => setPlate(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={handleAuth}
          disabled={!plate.trim() || authing}
          startIcon={authing ? <CircularProgress size={16} /> : null}
        >
          /kiosk/auth 호출
        </Button>
      </Box>

      {authResult && (
        <Box>
          {authResult.corrected && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <b>보정 발생</b>: <code>{authResult.originalPlate}</code> →{' '}
              <code>{authResult.licensePlate}</code>
            </Alert>
          )}
          {authResult.authorized ? (
            <Alert severity="success" sx={{ mb: 1 }}>
              <b>{authResult.memberName}</b> 회원 · {authResult.productName}
            </Alert>
          ) : (
            <Alert severity="error" sx={{ mb: 1 }}>
              인증 실패: {authResult.reason}
            </Alert>
          )}
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>raw response</Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              fontSize: 11,
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(authResult, null, 2)}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────
// 메인 페이지
// ──────────────────────────────────────────────
export default function LprTestPage() {
  const [tab, setTab] = useState('upload');
  const [authPlate, setAuthPlate] = useState('');

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        LPR 테스트
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        업로드 모드는 정지 사진으로, 라이브 모드는 카메라 스냅샷에서 실시간으로 우리 LPR(yolo)의
        인식 결과를 확인합니다. 결과 plate는 아래 인증 시뮬레이션에 자동 입력되어 백엔드 보정 동작도
        함께 검증할 수 있습니다.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth">
          <Tab value="upload" label="업로드" icon={<CloudUploadIcon />} iconPosition="start" />
          <Tab value="live" label="라이브 카메라" icon={<DirectionsCarIcon />} iconPosition="start" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 'upload' && <UploadMode onRecognized={setAuthPlate} />}
          {tab === 'live' && <LiveMode onRecognized={setAuthPlate} />}
        </Box>
      </Paper>

      <AuthSimulation plate={authPlate} setPlate={setAuthPlate} />
    </Box>
  );
}
